export type FollowRelation = {
  you_follow: boolean;
  follows_you: boolean;
};

export type SocialUser = {
  id: string;
  display_name: string | null;
};

export type PublicUserProfile = SocialUser & FollowRelation & {
  followers_count: number;
  following_count: number;
};

export type FollowListItem = SocialUser & Partial<FollowRelation> & {
  followed_at: string | null;
};

export type PageMeta = {
  current_page: number;
  per_page: number;
  total: number;
  last_page: number;
};

export type FollowListResponse = {
  data: FollowListItem[];
  meta: PageMeta;
};

export type FollowStatusesResponse = {
  data: Record<string, FollowRelation>;
};

export type ActivityType =
  | 'impression_created'
  | 'follow_created'
  | 'new_place_discovered'
  | 'photo_uploaded'
  | 'venue_offer_published'
  | 'venue_photo_published'
  | 'venue_event_published'
  | 'venue_post_published'
  | 'route_published'
  | 'route_completed'
  | 'achievement_unlocked'
  | 'city_leader_changed'
  | 'city_weekly_champion'
  | 'venue_leader_changed'
  | 'discovery_milestone'
  | 'achievement_highlight';

export type ActivityAuthor = {
  type?: 'user' | 'venue';
  id: string;
  display_name?: string | null;
  name?: string | null;
  avatar_url?: string | null;
};

export type ActivityEvent = {
  id: string;
  type: ActivityType;
  author: ActivityAuthor;
  venue: null | {
    id: string;
    name: string | null;
    category: string | null;
    address?: string | null;
  };
  city: null | {
    id: string;
    name: string;
  };
  payload: {
    impression_id?: string | null;
    venue_id?: string | null;
    venue_name?: string | null;
    venue_category?: string | null;
    verdict?: string | null;
    text?: string | null;
    followed_user_id?: string | null;
    followed_user_display_name?: string | null;
    media_id?: string | null;
    media_ids?: string[];
    media_count?: number;
    thumbnail_url?: string | null;
    media_url?: string | null;
    post_id?: string | null;
    title?: string | null;
    body?: string | null;
    cover_url?: string | null;
    content_version?: number;
    [key: string]: unknown;
  };
  created_at: string | null;
};

export type FeedResponse = {
  data: ActivityEvent[];
  meta: {
    next_cursor: string | null;
    has_more: boolean;
  };
};

export type LeaderboardPeriod = 'all' | 'week';

export type LeaderboardEntry = {
  rank: number;
  user: SocialUser & { avatar_url: string | null };
  xp: number;
  visits_count: number;
  level?: number | null;
};

export type LeaderboardResponse = {
  data: LeaderboardEntry[];
  meta: {
    period: LeaderboardPeriod;
    total: number;
  };
};

export type ProfileStats = {
  visits_count: number;
  impressions_count: number;
  discovered_count: number;
};

export type MyXpResponse = {
  xp: {
    summary: {
      total_xp: number;
      xp_total: number;
      streak_days: number;
      transaction_count: number;
      confirmed_visits: number;
      unique_cities: number;
      unique_venues: number;
    };
    cities: Array<{
      city: { id: string; name: string; slug: string; timezone: string };
      xp: number;
      confirmed_visits: number;
      unique_venues: number;
      last_visit_at: string | null;
    }>;
    venues: Array<{
      venue: { id: string; name: string | null; city_id: string };
      city: { id: string; name: string; slug: string };
      xp: number;
      confirmed_visits: number;
      last_visit_at: string | null;
    }>;
  };
  stats: ProfileStats;
};

export type MyVenueSort = 'visits' | 'xp' | 'recent';

export type MyVenueItem = {
  venue: { id: string; name: string | null };
  visits_count: number;
  xp_total: number;
  last_visit_at: string | null;
};

export type MyVenuesResponse = {
  data: MyVenueItem[];
  meta: {
    sort: MyVenueSort;
    total: number;
    limit: number;
  };
};

export type ProfileVisitPeriod = 'today' | 'week' | 'month' | 'all';

export type ProfileVisit = {
  id: string;
  local_date: string;
  status: string;
  receipt_count: number;
  total_amount: string;
  xp: number;
  first_receipt_at: string | null;
  last_receipt_at: string | null;
  confirmed_at: string | null;
  venue: {
    id: string;
    name: string | null;
    address: string | null;
    timezone: string | null;
  };
  city: {
    id: string;
    name: string;
    slug: string;
    timezone: string;
  };
};

export type MyVisitsResponse = {
  visits: {
    items: ProfileVisit[];
    pagination: {
      limit: number;
      has_more: boolean;
      next_cursor: string | null;
    };
  };
};

export type NotificationType =
  | 'new_follower'
  | 'followed_impression'
  | 'followed_discovery'
  | 'level_up'
  | 'moderation_violation_confirmed'
  | 'moderation_violation_overturned'
  | 'publication_hold_released';

export type NotificationItem = {
  id: string;
  type: NotificationType;
  actor: SocialUser | null;
  payload: {
    follower_id?: string | null;
    follower_display_name?: string | null;
    impression_id?: string | null;
    venue_id?: string | null;
    venue_name?: string | null;
    verdict?: string | null;
    [key: string]: unknown;
  };
  is_read: boolean;
  read_at: string | null;
  created_at: string | null;
};

export type NotificationsResponse = {
  data: NotificationItem[];
  meta: PageMeta & { unread_count: number };
};

export type NotificationPreferences = {
  new_follower: boolean;
  followed_impression: boolean;
  followed_discovery: boolean;
};

type ApiErrorPayload = {
  error?: string | { code?: string; message?: string };
  message?: string;
};

export class SocialApiError extends Error {
  readonly status: number;
  readonly payload: ApiErrorPayload | null;

  constructor(status: number, payload: ApiErrorPayload | null) {
    const errorMessage = typeof payload?.error === 'object' ? payload.error.message : null;
    super(errorMessage ?? payload?.message ?? `API request failed with status ${status}`);
    this.name = 'SocialApiError';
    this.status = status;
    this.payload = payload;
  }
}

let csrfToken: string | null = null;

async function parseJson(response: Response): Promise<unknown> {
  const contentType = response.headers.get('content-type') ?? '';
  return contentType.includes('application/json') ? response.json() : null;
}

async function ensureCsrf(): Promise<string> {
  if (csrfToken) {
    return csrfToken;
  }

  const response = await fetch('/api/v1/auth/csrf', {
    credentials: 'same-origin',
    headers: { Accept: 'application/json' },
  });
  const body = (await parseJson(response)) as { csrf_token?: string } | null;

  if (!response.ok || !body?.csrf_token) {
    throw new SocialApiError(response.status, body as ApiErrorPayload | null);
  }

  csrfToken = body.csrf_token;
  return csrfToken;
}

async function socialFetch<T>(url: string, options: RequestInit = {}): Promise<T> {
  const method = (options.method ?? 'GET').toUpperCase();
  const headers = new Headers(options.headers);
  headers.set('Accept', 'application/json');

  if (options.body !== undefined && options.body !== null && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }

  if (method !== 'GET' && method !== 'HEAD') {
    headers.set('X-CSRF-TOKEN', await ensureCsrf());
  }

  const response = await fetch(url, {
    ...options,
    method,
    credentials: 'same-origin',
    headers,
  });
  const body = await parseJson(response);

  if (!response.ok) {
    throw new SocialApiError(response.status, body as ApiErrorPayload | null);
  }

  return body as T;
}

export function fetchPublicUser(userId: string): Promise<PublicUserProfile> {
  return socialFetch(`/api/v1/users/${encodeURIComponent(userId)}`);
}

export function followUser(userId: string): Promise<{ you_follow: true; followers_count: number }> {
  return socialFetch(`/api/v1/users/${encodeURIComponent(userId)}/follow`, { method: 'POST' });
}

export function unfollowUser(userId: string): Promise<{ you_follow: false; followers_count: number }> {
  return socialFetch(`/api/v1/users/${encodeURIComponent(userId)}/follow`, { method: 'DELETE' });
}

export function fetchFollowers(userId: string, page = 1, perPage = 20): Promise<FollowListResponse> {
  return socialFetch(`/api/v1/users/${encodeURIComponent(userId)}/followers?page=${page}&per_page=${perPage}`);
}

export function fetchFollowing(userId: string, page = 1, perPage = 20): Promise<FollowListResponse> {
  return socialFetch(`/api/v1/users/${encodeURIComponent(userId)}/following?page=${page}&per_page=${perPage}`);
}

export function fetchFollowStatuses(userIds: string[]): Promise<FollowStatusesResponse> {
  const params = new URLSearchParams();
  userIds.forEach((id) => params.append('user_ids[]', id));
  return socialFetch(`/api/v1/me/follow-statuses?${params.toString()}`);
}

export function fetchCityFeed(city: string, cursor: string | null = null, limit = 20): Promise<FeedResponse> {
  const params = new URLSearchParams({ limit: String(limit) });
  if (cursor) params.set('cursor', cursor);
  return socialFetch(`/api/v1/cities/${encodeURIComponent(city)}/feed?${params.toString()}`);
}

export function fetchFollowingFeed(cursor: string | null = null, limit = 20): Promise<FeedResponse> {
  const params = new URLSearchParams({ limit: String(limit) });
  if (cursor) params.set('cursor', cursor);
  return socialFetch(`/api/v1/feed/following?${params.toString()}`);
}

export function fetchCityLeaderboard(cityId: string, period: LeaderboardPeriod = 'all', limit = 10): Promise<LeaderboardResponse> {
  const params = new URLSearchParams({ period, limit: String(limit) });
  return socialFetch(`/api/v1/cities/${encodeURIComponent(cityId)}/leaderboard?${params.toString()}`);
}

export function fetchMyXpProgress(): Promise<MyXpResponse> {
  return socialFetch('/api/v1/me/xp');
}

export function fetchMyActivity(cursor: string | null = null, limit = 10): Promise<FeedResponse> {
  const params = new URLSearchParams({ limit: String(limit) });
  if (cursor) params.set('cursor', cursor);
  return socialFetch(`/api/v1/me/activity?${params.toString()}`);
}

export function fetchMyVenues(sort: MyVenueSort = 'visits', limit = 20): Promise<MyVenuesResponse> {
  const params = new URLSearchParams({ sort, limit: String(limit) });
  return socialFetch(`/api/v1/me/venues?${params.toString()}`);
}

export function fetchMyVisits(period: ProfileVisitPeriod = 'all', limit = 20, cursor: string | null = null): Promise<MyVisitsResponse> {
  const params = new URLSearchParams({ period, limit: String(limit) });
  if (cursor) params.set('cursor', cursor);
  return socialFetch(`/api/v1/me/visits?${params.toString()}`);
}

export function fetchNotifications(page = 1, perPage = 20, isRead?: boolean): Promise<NotificationsResponse> {
  const params = new URLSearchParams({ page: String(page), per_page: String(perPage) });
  if (typeof isRead === 'boolean') params.set('is_read', String(isRead));
  return socialFetch(`/api/v1/me/notifications?${params.toString()}`);
}

export function fetchUnreadCount(): Promise<{ unread_count: number }> {
  return socialFetch('/api/v1/me/notifications/unread-count');
}

export function markNotificationRead(notificationId: string): Promise<{ id: string; is_read: true; read_at: string | null }> {
  return socialFetch(`/api/v1/me/notifications/${encodeURIComponent(notificationId)}/read`, { method: 'PATCH' });
}

export function markAllNotificationsRead(): Promise<{ marked_count: number }> {
  return socialFetch('/api/v1/me/notifications/mark-all-read', { method: 'POST' });
}

export function fetchNotificationPreferences(): Promise<NotificationPreferences> {
  return socialFetch('/api/v1/me/notification-preferences');
}

export function updateNotificationPreferences(preferences: NotificationPreferences): Promise<NotificationPreferences> {
  return socialFetch('/api/v1/me/notification-preferences', {
    method: 'PUT',
    body: JSON.stringify(preferences),
  });
}

export function notifyNotificationCountChanged(): void {
  window.dispatchEvent(new Event('hopper:notifications-updated'));
}
